// Kielioppi (grammar reference): one MDX file per theme in this folder, NN-slug.mdx
// (contract: AUTHORING.md). Like the lessons, only `meta` is loaded eagerly (it feeds the table of
// contents, the lesson → grammar links and the dictionary's "Lue lisää" links); the bodies are
// one lazy chunk per theme, loaded when its § page (/grammar/<theme id>) opens.
//
// Which lesson teaches a topic is NOT written here: each lesson's meta lists the topics it
// teaches (`grammar`, it is their main lesson) and comes back to (`grammarAlso`), and the topic's
// `lessons` are derived from those (docs/COURSE.md "Lesson hub").
import type { ComponentType } from 'react';
import { lessonMetas } from '../lessons';
import type { LessonFileMeta } from '../lessons';
import { loadGrammarTables, preloadGrammarTables as preloadTables } from './tables';

/** One topic of a theme: an anchor on the grammar page (`/grammar#<id>`). */
export interface GrammarTopic {
  /** Unique across all themes; also the element id and the URL hash. */
  id: string;
  titleFi: string;
  titleEn: string;
  /** Orders of the lessons that teach it (the first is the main one), from the lesson metas. */
  lessons: number[];
}

export interface GrammarThemeMeta {
  id: string;
  order: number;
  titleFi: string;
  titleEn: string;
  topics: GrammarTopic[];
  sources?: string[];
}

const metaModules = import.meta.glob<unknown>('./*.mdx', { query: '?meta', import: 'meta', eager: true });
const bodyModules = import.meta.glob<{ default: ComponentType }>('./*.mdx');

const str = (x: unknown): string => (typeof x === 'string' ? x : '');

function normalizeTopic(x: unknown): GrammarTopic | null {
  if (!x || typeof x !== 'object') return null;
  const t = x as Record<string, unknown>;
  const id = str(t['id']);
  if (!id) return null;
  const titleFi = str(t['titleFi']) || str(t['titleEn']) || id;
  return { id, titleFi, titleEn: str(t['titleEn']) || titleFi, lessons: [] };
}

/** Lesson orders per topic id: the main lesson (its meta's `grammar`) first, then `grammarAlso` in order. */
export function topicLessons(metas: readonly LessonFileMeta[]): Map<string, number[]> {
  const main = new Map<string, number>();
  const also = new Map<string, number[]>();
  for (const m of [...metas].sort((a, b) => a.order - b.order)) {
    for (const id of m.grammar ?? []) if (!main.has(id)) main.set(id, m.order);
    for (const id of m.grammarAlso ?? []) also.set(id, [...(also.get(id) ?? []), m.order]);
  }
  const out = new Map<string, number[]>();
  for (const id of new Set([...main.keys(), ...also.keys()])) {
    const first = main.get(id);
    out.set(id, [...(first ? [first] : []), ...(also.get(id) ?? []).filter((n) => n !== first)]);
  }
  return out;
}

/** A theme meta made safe to use (hand-written); null when unusable. */
export function normalizeGrammarMeta(raw: unknown, path = ''): GrammarThemeMeta | null {
  if (!raw || typeof raw !== 'object') return null;
  const m = raw as Record<string, unknown>;
  const fromPath = /(\d+)-([^/]*)\.mdx$/.exec(path);
  const id = str(m['id']) || (fromPath ? fromPath[2]! : '');
  const order = typeof m['order'] === 'number' ? m['order'] : fromPath ? Number(fromPath[1]) : NaN;
  if (!id || !Number.isFinite(order)) return null;
  const titleFi = str(m['titleFi']) || str(m['titleEn']) || id;
  const topics = (Array.isArray(m['topics']) ? m['topics'] : []).map(normalizeTopic).filter((t): t is GrammarTopic => !!t);
  return {
    id,
    order,
    titleFi,
    titleEn: str(m['titleEn']) || titleFi,
    topics,
    ...(Array.isArray(m['sources']) ? { sources: m['sources'].filter((s): s is string => typeof s === 'string') } : {}),
  };
}

const pathById = new Map<string, string>();

/** Every grammar theme, in order. A topic id used twice keeps its first theme. */
export const grammarThemes: readonly GrammarThemeMeta[] = (() => {
  const out: GrammarThemeMeta[] = [];
  const seen = new Set<string>();
  const byTopic = topicLessons(lessonMetas);
  for (const [key, raw] of Object.entries(metaModules).sort(([a], [b]) => a.localeCompare(b))) {
    const path = key.replace(/\?.*$/, '');
    const meta = normalizeGrammarMeta(raw, path);
    if (!meta || pathById.has(meta.id)) {
      if (import.meta.env.DEV) console.warn(`[grammar] ${path}: missing, invalid or duplicate meta`);
      continue;
    }
    meta.topics = meta.topics.filter((t) => !seen.has(t.id) && seen.add(t.id)).map((t) => ({ ...t, lessons: byTopic.get(t.id) ?? [] }));
    pathById.set(meta.id, path);
    out.push(meta);
  }
  return out.sort((a, b) => a.order - b.order);
})();

export interface TopicRef {
  theme: GrammarThemeMeta;
  topic: GrammarTopic;
}

export const GRAMMAR_PATH = '/grammar';

/** A theme's own page (one § per route: /grammar/sounds). */
export function themePath(themeId: string): string {
  return `${GRAMMAR_PATH}/${encodeURIComponent(themeId)}`;
}

/** The URL of a topic: its theme's page, at the topic (/grammar/sounds#stress). */
export function grammarHref(topicId: string): { pathname: string; hash: string } {
  const ref = topicById(topicId);
  return { pathname: ref ? themePath(ref.theme.id) : GRAMMAR_PATH, hash: `#${topicId}` };
}

export function topicById(id: string): TopicRef | undefined {
  for (const theme of grammarThemes) {
    const topic = theme.topics.find((t) => t.id === id);
    if (topic) return { theme, topic };
  }
  return undefined;
}

export function themeById(id: string): GrammarThemeMeta | undefined {
  return grammarThemes.find((t) => t.id === id);
}

/** Topics a lesson teaches (its order is in the topic's `lessons`), main lesson first. */
export function topicsForLesson(order: number): TopicRef[] {
  const out: TopicRef[] = [];
  for (const theme of grammarThemes) for (const topic of theme.topics) if (topic.lessons.includes(order)) out.push({ theme, topic });
  // topics for which this is the main (first) lesson come first
  return out.sort((a, b) => Number(b.topic.lessons[0] === order) - Number(a.topic.lessons[0] === order));
}

const loaded = new Map<string, ComponentType | null>();

/** A theme body, loaded on demand. */
export async function loadGrammarTheme(id: string): Promise<ComponentType | null> {
  if (loaded.has(id)) return loaded.get(id)!;
  const path = pathById.get(id);
  const load = path ? bodyModules[path] : undefined;
  if (!load) return null;
  // with its generated tables, built at build time (./tables.ts; without them the § generates its forms)
  const [mod] = await Promise.all([load(), loadGrammarTables(id)]);
  const body = mod.default;
  loaded.set(id, body);
  return body;
}

/**
 * Every §'s tables, ahead of use (a start-up warm-up): opening a § then only renders. Resolves to
 * the number of § files in use (0 when there are none for this build); never throws.
 */
export function preloadGrammarTables(): Promise<number> {
  return preloadTables(grammarThemes.map((t) => t.id));
}

/** A theme body already loaded (a § visited before renders at once, without a loading line). */
export function loadedGrammarTheme(id: string): ComponentType | null | undefined {
  return loaded.get(id);
}
